import type { ItemStubsResponseDTO } from '@tempo-io/roadmap-dto';
import * as React from 'react';
import type { ItemsTableRow } from '../types';

export const useTreeData = (dataset?: ItemStubsResponseDTO) => {
  const { parentMap, skeletonRows } = React.useMemo(() => {
    if (!dataset) {
      return {
        parentMap: new Map<number, number | undefined>(),
        skeletonRows: [] as ItemsTableRow[],
      };
    }

    const map = new Map<number, number | undefined>();
    const allIds = new Set(dataset.map((stub) => stub.id));
    const rowById = new Map<number, ItemsTableRow>();

    for (const stub of dataset) {
      const parentId = stub.parentId && allIds.has(stub.parentId) ? stub.parentId : undefined;
      map.set(stub.id, parentId);
      const { parentManagedByIntegration: _treeSetLock, ...rest } = stub;
      rowById.set(stub.id, { ...rest, parentId });
    }

    // Parents must come before their descendants in the rows array. MUI's
    // tree rebuild creates an auto-generated group with childrenExpanded=false
    // for any path step whose row hasn't been processed yet, and the real
    // parent's expansion state is then lost when it gets promoted.
    const childrenByParent = new Map<number | undefined, number[]>();
    for (const stub of dataset) {
      const parentId = map.get(stub.id);
      const siblings = childrenByParent.get(parentId);
      if (siblings) siblings.push(stub.id);
      else childrenByParent.set(parentId, [stub.id]);
    }

    const rows: ItemsTableRow[] = [];
    const stack: number[] = [...(childrenByParent.get(undefined) ?? [])].reverse();
    while (stack.length > 0) {
      const id = stack.pop();
      if (id === undefined) break;
      const row = rowById.get(id);
      if (!row) continue;
      rows.push(row);
      const children = childrenByParent.get(id);
      if (children) {
        for (let i = children.length - 1; i >= 0; i--) {
          const childId = children[i];
          if (childId !== undefined) stack.push(childId);
        }
      }
    }

    return { parentMap: map, skeletonRows: rows };
  }, [dataset]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dataset change must clear cached paths so they are recomputed for the new tree structure
  const itemsPaths = React.useMemo(() => new Map<number, string[]>(), [dataset]);

  const getTreeDataPath = React.useCallback(
    (row: ItemsTableRow): readonly string[] => {
      if (!parentMap.size) return [];

      if (!itemsPaths.has(row.id)) {
        const path: string[] = [String(row.id)];
        let parentId = parentMap.get(row.id);
        while (parentId) {
          path.unshift(String(parentId));
          parentId = parentMap.get(parentId);
        }
        itemsPaths.set(row.id, path);
      }

      return itemsPaths.get(row.id) ?? [];
    },
    [itemsPaths, parentMap],
  );

  return { skeletonRows, getTreeDataPath };
};
